import React, { useState, useCallback, useEffect } from 'react';
import { Typography } from '../../interfaces/typography';
import { SESSION_METRICS_COLUMNS } from "interfaces/table";
import { SessionRequestType } from '../../lib/redux/sessions/data/data-slice';
import { useAIGenerationFunctions } from '../../lib/redux/sessions/data/ai-generation-hook';
import { useCustomMetricFunctions } from '../../lib/redux/sessions/data/custom-metric-hook';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { FileText, Mail, Lightbulb, Copy, Check, BarChart2, Dumbbell, Settings as SettingsIcon, Database } from 'lucide-react';
import { ScoreMetric } from './TranscriptView/ScoreMetric';
import { SessionDetailView, viewCanSeeAllAutomations } from '../History/views';
import { useNavigate } from 'react-router-dom';
import { PracticeBotTest } from './PracticeBot/PracticeBot';
import { SessionDataResult } from 'interfaces/sessions';
import { useUserData } from '../../lib/redux/users/user-data-hook';

interface AIGenerationSectionProps {
  sessionData: SessionDataResult;
  view: SessionDetailView;
}

type AIContentType = {
  type: SessionRequestType | 'SCORE' | 'PRACTICE' | 'SESSION_STATE';
  title: string;
  description: string;
  icon: React.ReactNode;
  metricColumn?: SESSION_METRICS_COLUMNS;
}

export const AIGenerationSection: React.FC<AIGenerationSectionProps> = ({ sessionData, view }) => {
  const { requestAI } = useAIGenerationFunctions();
  const { requestCustomMetric } = useCustomMetricFunctions();
  const [activeTab, setActiveTab] = useState<SessionRequestType | 'SCORE' | 'PRACTICE' | 'SESSION_STATE' | null>(() => {
    // Try to get the saved tab from localStorage
    const savedTab = localStorage.getItem('aiGenerationActiveTab');
    return savedTab as SessionRequestType | 'SCORE' | 'PRACTICE' | 'SESSION_STATE' | null;
  });
  const [isExpanded, setIsExpanded] = useState(true);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  
  // Get request states from Redux store
  const sessionDetails = useSelector((state: RootState) => 
    state.sessionDetails.sessionIdToDetails[sessionData.session.session_id]
  );
  
  // Get admin status and custom definitions
  const { isAdmin } = useUserData();
  const customDefinitions = useSelector((state: RootState) => state.customDefinitions.value);
  const hasCustomScores = customDefinitions && customDefinitions.length > 0;

  // Check if there are enough turns in the conversation to generate AI content
  // Require at least 2 turns for a meaningful conversation
  const hasEnoughTurns = sessionData.turns && sessionData.turns.length >= 3;

  // Check if we can show score metrics
  const canShowScore = hasCustomScores || isAdmin;
  
  // Check if we have session state data
  const hasSessionState = sessionData.session_state && sessionData.session_state.states && Object.keys(sessionData.session_state.states).length > 0;

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    if (activeTab) {
      localStorage.setItem('aiGenerationActiveTab', activeTab);
    }
  }, [activeTab]);

  // Set initial active tab based on available content
  useEffect(() => {
    if (!activeTab && hasEnoughTurns) {
      // Check if any content already exists and set that tab as active
      if (sessionData.sales_metric?.[SESSION_METRICS_COLUMNS.GEN_SUMMARY_TEXT]) {
        setActiveTab(SessionRequestType.SUMMARY);
      } else if (sessionData.sales_metric?.[SESSION_METRICS_COLUMNS.GEN_EMAIL_TEXT]) {
        setActiveTab(SessionRequestType.EMAIL);
      } else if (sessionData.sales_metric?.[SESSION_METRICS_COLUMNS.GEN_COACHING_TEXT]) {
        setActiveTab(SessionRequestType.COACHING);
      } else {
        // Default to summary tab if no content exists
        setActiveTab(SessionRequestType.SUMMARY);
      }
    } else if (!hasEnoughTurns) {
      // Clear active tab if there's not enough data
      setActiveTab(null);
      setIsExpanded(false);
    }
  }, [sessionData, activeTab, hasEnoughTurns]);

  const summaryContentType = {
    type: SessionRequestType.SUMMARY,
    title: 'Summary',
    description: 'AI-generated summary of the conversation',
    icon: <FileText size={16} />,
    metricColumn: SESSION_METRICS_COLUMNS.GEN_SUMMARY_TEXT,
  }
  const scoreContentType: AIContentType = {
    type: 'SCORE',
    title: 'Score',
    description: 'Metrics and scores for this conversation',
    icon: <BarChart2 size={16} />,
  }
  const emailContentType = {
    type: SessionRequestType.EMAIL,
    title: 'Email',
    description: 'AI-generated follow-up email',
    icon: <Mail size={16} />,
    metricColumn: SESSION_METRICS_COLUMNS.GEN_EMAIL_TEXT,
  }
  const coachingContentType = {
    type: SessionRequestType.COACHING,
    title: 'Coaching',
    description: 'AI-generated coaching suggestions',
    icon: <Lightbulb size={16} />,
    metricColumn: SESSION_METRICS_COLUMNS.GEN_COACHING_TEXT,
  }

  const practiceBotContentType: AIContentType = {
    type: 'PRACTICE',
    title: 'Practice',
    description: 'Practice conversation with an AI bot',
    icon: <Dumbbell size={16} />,
  }

  const sessionStateContentType: AIContentType = {
    type: 'SESSION_STATE',
    title: 'Session State',
    description: 'Session state and payload data',
    icon: <Database size={16} />,
  }

  const aiContentTypes: AIContentType[] = []
  if (viewCanSeeAllAutomations(view)) {
    aiContentTypes.push(summaryContentType);
    // Only add score if user is admin or has custom scores
    if (canShowScore) {
      aiContentTypes.push(scoreContentType);
    }
    aiContentTypes.push(emailContentType, coachingContentType, practiceBotContentType);
    // Add session state tab if data is available
    if (hasSessionState) {
      aiContentTypes.push(sessionStateContentType);
    }
  } else {
    aiContentTypes.push(summaryContentType, emailContentType);
    // Add session state tab if data is available for limited views too
    if (hasSessionState) {
      aiContentTypes.push(sessionStateContentType);
    }
  }

  const handleRequestAI = useCallback(async (type: SessionRequestType) => {
    if (!sessionData || !hasEnoughTurns) return;
    
    try {
      await requestAI(sessionData.session.session_id, type);
    } catch (error) {
      console.error(`Error generating ${type}:`, error);
    }
  }, [sessionData, hasEnoughTurns, requestAI]);

  const getContentForType = (type: SessionRequestType): string | null => {
    if (!sessionData.sales_metric) return null;
    
    switch (type) {
      case SessionRequestType.SUMMARY:
        return sessionData.sales_metric[SESSION_METRICS_COLUMNS.GEN_SUMMARY_TEXT] || null;
      case SessionRequestType.EMAIL:
        return sessionData.sales_metric[SESSION_METRICS_COLUMNS.GEN_EMAIL_TEXT] || null;
      case SessionRequestType.COACHING:
        return sessionData.sales_metric[SESSION_METRICS_COLUMNS.GEN_COACHING_TEXT] || null;
      default:
        return null;
    }
  };

  // Check if a specific AI type is currently loading
  const isTypeLoading = (type: SessionRequestType): boolean => {
    return !!sessionDetails?.requestStates?.[type]?.isLoading;
  };

  const handleTabClick = (type: SessionRequestType | 'SCORE' | 'PRACTICE' | 'SESSION_STATE') => {
    if (!hasEnoughTurns && type !== 'SESSION_STATE') return;
    
    if (activeTab === type && isExpanded) {
      // Only toggle expansion, don't clear the active tab
      setIsExpanded(false);
    } else {
      setActiveTab(type);
      setIsExpanded(true);
    }
  };


  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Function to navigate to custom scores settings
  const goToCustomScores = () => {
    navigate('/settings?tab=Custom Scores');
  };

  // Render content based on active tab
  const renderContent = () => {
    if (!activeTab) return null;

    if (activeTab === 'PRACTICE') {
      return (
        <PracticeBotTest sessionData={sessionData}/>
      );
    }

    if (activeTab === 'SESSION_STATE') {
      if (!hasSessionState) {
        return (
          <div className="px-3 py-2">
            <div className="flex items-center justify-center py-3 text-center">
              <Typography variant="mediumParagraph" color="gray">
                No session state data available for this conversation
              </Typography>
            </div>
          </div>
        );
      }

      return (
        <div className="px-3 py-2">
          {/* Session state fields in a compact, wrapped layout */}
          <div className="max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(sessionData.session_state!.states).map(([key, value]) => (
                <div 
                  key={key}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  <Typography 
                    variant="smallParagraph" 
                    fontWeight="600" 
                    color="#4B5563"
                    className="whitespace-nowrap"
                  >
                    {key}:
                  </Typography>
                  <Typography 
                    variant="smallParagraph" 
                    color="#111827"
                    className="select-text max-w-[150px] truncate" 
                    title={value}
                  >
                    {value || '(empty)'}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'SCORE') {
      return (
        <div className="px-3 py-2">
          {hasCustomScores ? (
            sessionData.custom_metric ? (
              <ScoreMetric 
                customDetail={sessionData.custom_detail}
                customMetric={sessionData.custom_metric}
                teamId={sessionData.session.team_id || undefined}
              />
            ) : (
              <div className="flex items-center justify-center py-3">
                {isTypeLoading(SessionRequestType.CUSTOM_SCORE) ? (
                  <div className="flex items-center gap-2">
                    <div className="relative w-5 h-5">
                      <div className="absolute top-0 left-0 w-full h-full border-2 border-gray-200 rounded-full"></div>
                      <div className="absolute top-0 left-0 w-full h-full border-2 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
                    </div>
                    <Typography variant="mediumParagraph" color="gray">
                      Generating metrics...
                    </Typography>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Typography variant="mediumParagraph" color="gray">
                      No metrics available yet
                    </Typography>
                    <button
                      onClick={() => requestCustomMetric(sessionData.session.session_id)}
                      className="px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                      disabled={!hasEnoughTurns}
                    >
                      <Typography variant="smallParagraph" color="white">
                        Generate Metrics
                      </Typography>
                    </button>
                  </div>
                )}
              </div>
            )
          ) : isAdmin ? (
            <div className="flex items-center justify-center gap-3 py-4">
              <Typography variant="mediumParagraph" color="gray">
                No custom scores have been configured for your team
              </Typography>
              <button
                onClick={goToCustomScores}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                <SettingsIcon size={14} />
                <Typography variant="smallParagraph" color="white">
                  Configure Custom Scores
                </Typography>
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center py-3 text-center">
              <Typography variant="mediumParagraph" color="gray">
                No metrics available for this conversation
              </Typography>
            </div>
          )}
        </div>
      );
    }

    // Handle AI content types (Summary, Email, Coaching)
    const contentType = activeTab as SessionRequestType;
    return (
      <div className="px-3 py-2">
        {getContentForType(contentType) ? (
          <div className="whitespace-pre-wrap text-gray-700 max-h-48 overflow-y-auto p-3 bg-white rounded border border-gray-100 relative group select-text">
            {contentType === SessionRequestType.EMAIL && (
              <button 
                onClick={() => copyToClipboard(getContentForType(contentType) || '')}
                className="absolute top-2 right-2 p-1 rounded-md bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-indigo-600 transition-colors opacity-0 group-hover:opacity-100"
                aria-label="Copy email content"
              >
                {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
              </button>
            )}
            
            {contentType === SessionRequestType.EMAIL ? (
              <div className="select-text">
                <div className="border-b border-gray-100 pb-1.5 mb-2">
                  <Typography variant="mediumParagraph" fontWeight="600" className="select-text">
                    Subject: {getContentForType(contentType)?.split('\n')[0].replace('Subject: ', '')}
                  </Typography>
                </div>
                <Typography variant="mediumParagraph" className="select-text">
                  {getContentForType(contentType)?.split('\n').slice(1).join('\n')}
                </Typography>
              </div>
            ) : (
              <Typography variant="mediumParagraph" className="select-text">
                {getContentForType(contentType)}
              </Typography>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center py-3">
            {isTypeLoading(contentType) ? (
              <div className="flex items-center gap-2">
                <div className="relative w-5 h-5">
                  <div className="absolute top-0 left-0 w-full h-full border-2 border-gray-200 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-full h-full border-2 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
                </div>
                <Typography variant="mediumParagraph" color="gray">
                  Generating {contentType.toLowerCase()}...
                </Typography>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Typography variant="mediumParagraph" color="gray">
                  No {contentType.toLowerCase()} available yet
                </Typography>
                <button
                  onClick={() => handleRequestAI(contentType)}
                  className="px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                  disabled={!hasEnoughTurns}
                >
                  <Typography variant="smallParagraph" color="white">
                    Generate
                  </Typography>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full border-b border-gray-100 bg-white relative z-[20]">
      {/* Tabs header with toggle arrow */}
      <div className="flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center w-full justify-around">
          {aiContentTypes.map((contentType) => {
            const isActive = activeTab === contentType.type && isExpanded;
            const isLoading = contentType.type !== 'SCORE' && contentType.type !== 'PRACTICE' && contentType.type !== 'SESSION_STATE' && isTypeLoading(contentType.type as SessionRequestType);
            const isDisabled = !hasEnoughTurns && contentType.type !== 'SESSION_STATE';
            
            return (
              <div key={contentType.type} className="relative group w-full">
                <button
                  onClick={() => handleTabClick(contentType.type)}
                  className={`justify-center w-full text-center flex items-center gap-1.5 px-3 py-2 transition-colors
                    ${isActive 
                      ? 'text-indigo-600 border-b-2 border-indigo-600 -mb-px' 
                      : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                    ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  disabled={isDisabled}
                >
                  <span className="text-current">{contentType.icon}</span>
                  <Typography 
                    variant="mediumParagraph" 
                    fontWeight={isActive ? "600" : "400"}
                    color={isActive ? "#4F46E5" : "gray"}
                  >
                    {contentType.title}
                  </Typography>
                  {isLoading && (
                    <div className="w-3 h-3 rounded-full border-2 border-indigo-600 border-t-transparent animate-spin"></div>
                  )}
                </button>
              </div>
            );
          })}
        </div>
    </div>
      
      {/* Collapsible content - show if there's enough data OR if it's session state */}
      {isExpanded && activeTab && (hasEnoughTurns || activeTab === 'SESSION_STATE') && (
        <div className="animate-fadeIn">
          {renderContent()}
        </div>
      )}
    </div>
  );
}; 